import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Timestamp } from 'firebase/firestore';
import { UserStateInterface, ResponseUserInterface, ResponseUserTreeReferralsInterface } from '../../domain/interfaces';
import { Filter } from '../../../../../domain/interfaces/PaginationBaseInterface';

const initialState: UserStateInterface = {
    users: [],
    usersTree: null,
    loading: false,
    error: null,
    pagination: {
        totalResults: 0,
        totalPages: 0,
        currentPage: 1,
    },
    paginationRequest: {
        pagination: 10,
        lastVisibleId: null,
        filters: [],
        pageCursors: { 1: null },
    },
};

const UserSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        fetchUsersStartReducer(state) {
            state.loading = true;
            state.error = null;
        },
        fetchUsersSuccessReducer(state, action: PayloadAction<ResponseUserInterface>) {
            state.loading = false;
            // Convertimos el timestamp de Firestore a un objeto Date de JavaScript
            state.users = action.payload.users.map(user => {
                // Reconstruimos el objeto Timestamp de Firestore a partir de los segundos y nanosegundos
                const { _seconds: seconds, _nanoseconds: nanoseconds } = user.createdAt as unknown as { _seconds: number, _nanoseconds: number };
                const firestoreTimestamp = new Timestamp(seconds, nanoseconds);
                const jsDate = firestoreTimestamp.toDate();
                return { ...user, createdAt: jsDate };
            });
            state.pagination.totalResults = action.payload.totalUsers;
            state.pagination.totalPages = action.payload.totalPages
            
            const nextPage = state.pagination.currentPage + 1;
            const lastVisibleId = action.payload.users.at(-1)?.id ?? null;
            state.paginationRequest.pageCursors[nextPage] = lastVisibleId;
            
            state.paginationRequest.lastVisibleId = lastVisibleId;
        },
        setCurrentPageReducer: (state, action: PayloadAction<number>) => {
            state.pagination.currentPage = action.payload
        },
        fetchUsersFailureReducer(state, action: PayloadAction<string>) {
            state.loading = false;
            state.error = action.payload;
        },
        clearUsersErrorReducer(state) {
            state.error = null;
        },
        setFilterReducer: (state, action: PayloadAction<Filter>) => {
            const { field, value } = action.payload;
            const existingFilterIndex = state.paginationRequest.filters.findIndex(f => f.field === field);

            if (existingFilterIndex !== -1) {
                if (value) {
                    state.paginationRequest.filters[existingFilterIndex].value = value;
                } else {
                    state.paginationRequest.filters.splice(existingFilterIndex, 1);
                }
            } else if (value) {
                state.paginationRequest.filters.push(action.payload);
            }
            // Reset pagination and cursors when filters change
            state.pagination.currentPage = 1;
            state.paginationRequest.pageCursors = { 1: null };
            state.paginationRequest.lastVisibleId = null;
        },
        fetchUsersTreeReferralsSuccessReducer(state, action: PayloadAction<ResponseUserTreeReferralsInterface>) {
            state.loading = false;
            state.usersTree = action.payload.users;
        },
        exportUsersStartReducer(state) {
            state.loading = true;
            state.error = null;
        },
        exportUsersSuccessReducer(state) {
            state.loading = false;
        },
        exportUsersFailureReducer(state, action: PayloadAction<string>) {
            state.loading = false;
            state.error = action.payload;
        },
    },
});

export const {
    fetchUsersStartReducer,
    fetchUsersSuccessReducer,
    fetchUsersFailureReducer,
    clearUsersErrorReducer,
    setCurrentPageReducer,
    setFilterReducer,
    fetchUsersTreeReferralsSuccessReducer,
    exportUsersStartReducer,
    exportUsersSuccessReducer,
    exportUsersFailureReducer,
} = UserSlice.actions;

export default UserSlice.reducer;
